import { getUserGroups } from './auth';

function normalize(value) {
  return String(value || '').trim();
}

function normalizeKey(value) {
  return normalize(value).toLowerCase().replace(/[\s-]+/g, '_');
}

function formatDisplayLabel(value, fallback = 'Not available') {
  const normalized = normalize(value);
  if (!normalized) {
    return fallback;
  }

  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isEmailLike(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalize(value));
}

function firstNonEmailName(values, email) {
  const normalizedEmail = normalize(email).toLowerCase();

  return values
    .map((value) => normalize(value))
    .find((value) => value && value.toLowerCase() !== normalizedEmail && !isEmailLike(value));
}

export function getUserEmail(user, partnerProfile) {
  return normalize(partnerProfile?.email || user?.email || user?.preferred_username);
}

export function getUserDisplayName(user, partnerProfile) {
  const email = getUserEmail(user, partnerProfile);
  const combinedName = [user?.given_name, user?.family_name].map(normalize).filter(Boolean).join(' ');

  return (
    firstNonEmailName([
      partnerProfile?.full_name,
      user?.name,
      combinedName,
      user?.nickname,
    ], email) ||
    normalize(partnerProfile?.full_name) ||
    'AMX user'
  );
}

export function getPartnerMemberships(partnerProfile) {
  return Array.isArray(partnerProfile?.memberships) ? partnerProfile.memberships : [];
}

export function getAccountPartnerMembership(partnerProfile) {
  const memberships = getPartnerMemberships(partnerProfile);
  return memberships[0] || null;
}

export function getMembershipDisplayName(membership, fallback = 'Associated partner') {
  return normalize(membership?.display_name || membership?.partner_code || membership?.partner_id) || fallback;
}

export function getAccountTypeValue(user, partnerProfile) {
  const storedType = normalizeKey(partnerProfile?.account_type || user?.account_type);
  if (storedType === 'amx_admin' || storedType === 'amx_user') {
    return storedType;
  }

  const groups = getUserGroups(user).map(normalizeKey);
  if (groups.includes('amx_admin')) {
    return 'amx_admin';
  }
  if (groups.includes('amx_user')) {
    return 'amx_user';
  }

  return '';
}

export function getAccountTypeLabel(user, partnerProfile) {
  const accountType = getAccountTypeValue(user, partnerProfile);

  if (accountType === 'amx_admin') {
    return 'AMX Admin';
  }
  if (accountType === 'amx_user') {
    return 'AMX Partner';
  }

  return 'Not available';
}

export function getBusinessRoleLabel(role, fallback = 'Not available') {
  const normalizedRole = normalizeKey(role);

  if (normalizedRole === 'partner_admin' || normalizedRole === 'amx_admin' || normalizedRole === 'admin') {
    return 'Admin';
  }
  if (normalizedRole === 'partner_user' || normalizedRole === 'amx_user' || normalizedRole === 'user') {
    return 'User';
  }

  return formatDisplayLabel(role, fallback);
}

export function getCurrentAccountRoleLabel(user, partnerProfile, membership = getAccountPartnerMembership(partnerProfile)) {
  if (getAccountTypeValue(user, partnerProfile) === 'amx_admin') {
    return 'Admin';
  }

  return getBusinessRoleLabel(membership?.role || partnerProfile?.role || user?.role, 'User');
}

export function getUserAffiliationLabel(partnerProfile) {
  const accountMembership = getAccountPartnerMembership(partnerProfile);

  if (accountMembership) {
    return getMembershipDisplayName(accountMembership);
  }

  if (normalizeKey(partnerProfile?.account_type) === 'amx_admin') {
    return 'AMX account';
  }

  if (partnerProfile?.status) {
    return `${normalize(partnerProfile.status)} account`;
  }

  return 'AMX account';
}

export function getInitials(label) {
  const words = normalize(label).split(/\s+/).filter(Boolean);

  if (!words.length) {
    return 'A';
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}
